// Strictly validates the runtime auth config injected via window.ENV by
// public/config.js (loaded by a <script> before this bundle). The bootstrap in
// src/index.js calls readAuthConfig() and FAILS CLOSED if it throws — it must
// never fall through to rendering an unauthenticated app.

export class AuthConfigError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AuthConfigError';
  }
}

function isNonEmptyString(v) {
  return typeof v === 'string' && v.trim().length > 0;
}

// A value that still contains a CI placeholder token (e.g. "__auth_client_id__")
// was never substituted for this environment — treat it as missing.
function looksUnsubstituted(v) {
  return typeof v === 'string' && v.includes('__');
}

export function readAuthConfig() {
  const env = typeof window !== 'undefined' ? window.ENV : undefined;
  if (!env || typeof env !== 'object') {
    throw new AuthConfigError(
      'window.ENV is missing — config.js did not load. Check the <script> tag in index.html.'
    );
  }

  const auth = env.AUTH;
  if (!auth || typeof auth !== 'object') {
    throw new AuthConfigError('window.ENV.AUTH is missing or malformed.');
  }

  const { clientId, authority, knownAuthorities, cacheLocation, resources } = auth;

  if (!isNonEmptyString(clientId) || looksUnsubstituted(clientId)) {
    throw new AuthConfigError('AUTH.clientId is missing or was not substituted.');
  }
  if (!isNonEmptyString(authority) || looksUnsubstituted(authority)) {
    throw new AuthConfigError('AUTH.authority is missing or was not substituted.');
  }
  if (
    !Array.isArray(knownAuthorities) ||
    knownAuthorities.length === 0 ||
    !knownAuthorities.every(isNonEmptyString) ||
    knownAuthorities.some(looksUnsubstituted)
  ) {
    throw new AuthConfigError(
      'AUTH.knownAuthorities must be a non-empty array of authority hostnames.'
    );
  }
  if (!resources || typeof resources !== 'object') {
    throw new AuthConfigError('AUTH.resources is missing.');
  }

  const resourceNames = Object.keys(resources);
  if (resourceNames.length === 0) {
    throw new AuthConfigError('AUTH.resources has no entries.');
  }
  for (const name of resourceNames) {
    const r = resources[name];
    if (
      !r ||
      typeof r !== 'object' ||
      !isNonEmptyString(r.endpoint) ||
      !Array.isArray(r.scopes) ||
      r.scopes.length === 0 ||
      !r.scopes.every(isNonEmptyString) ||
      r.scopes.some(looksUnsubstituted)
    ) {
      throw new AuthConfigError(
        `AUTH.resources.${name} must have an "endpoint" string and a non-empty "scopes" array.`
      );
    }
  }

  return {
    clientId,
    authority,
    knownAuthorities,
    // Default to sessionStorage unless explicitly asked for cross-tab SSO.
    cacheLocation: cacheLocation === 'localStorage' ? 'localStorage' : 'sessionStorage',
    resources,
    b2cPolicies: auth.b2cPolicies, // may be undefined — B2C hook left for later
  };
}
